<script lang="ts">
  import { onMount } from "svelte";
  import type { CardConnection, CardEdge } from "$lib/gql/genCms/codegen";
  import { CardType } from "$lib/gql/genCms/codegen";
  import EditorialCard from "$lib/components/Cards/EditorialCard/EditorialCard.svelte";
  import ProductCard from "$lib/components/Cards/ProductCard/ProductCard.svelte";
  import { ImageConfig3Card } from "$lib/components/Cards/ProductCard/ProductCard.image-config";
  import { findSizeGuideCategory } from "$lib/utils/findSizeGuideCategory";

  export let cards: CardConnection;
  export let url: string;

  let allCards: CardEdge[] = cards?.edges?.filter(
    c => c?.node?.content?.product !== null,
  );
  let singleCardOnly = allCards?.length >= 1 && allCards?.length <= 3;
  let slicedCards;

  let fontVariant = "overline";
  let viewportWidth: number;
  let isLgViewport = false;
  let lgWidth = 1280;
  let xlWidth = 1600;
  let paddingLg = false;
  let imageConfig = ImageConfig3Card;

  onMount(() => {
    const bodyStyle = getComputedStyle(document.body);
    lgWidth = Number(bodyStyle.getPropertyValue("--lg").replace(/\D/g, ""));
  });

  $: isLgViewport = viewportWidth >= lgWidth && viewportWidth < xlWidth;

  $: if (isLgViewport && singleCardOnly) {
    fontVariant = "h3-2";
    paddingLg = true;
  }

  $: if (singleCardOnly) {
    slicedCards = allCards.slice(0, 1);
  } else {
    slicedCards = allCards.slice(0, 4);
  }
</script>

<style lang="scss">
  .card-wrapper {
    background-color: $surface;
    padding: 16px;
    border: 1px solid $background;

    @include retina {
      border: 0.5px solid $background;
    }

    &--multiple {
      display: grid;
      justify-content: center;
      grid-gap: 40px;
      grid-template-columns: 1fr 1fr;
    }

    @include breakpoint(md) {
      padding: 20px;
    }

    @include breakpoint(lg) {
      padding: 48px;
      grid-gap: 48px;
    }

    @include breakpoint(xl) {
      padding: 64px;
      grid-gap: 64px;
    }
  }
</style>

<svelte:window bind:innerWidth={viewportWidth} />

{#if allCards && allCards.length >= 1}
  <div class={`card-wrapper ${singleCardOnly ? "" : "card-wrapper--multiple"}`}>
    {#each slicedCards as card}
      {#if card.node.type === CardType.Product && card.node.content.product && card.node.content.product !== null}
        <!-- Not possible to check against collections here so safer to use canonical url -->
        <ProductCard
          title={card.node.content.product.title}
          subtitle={card.node.content.product.subtitle}
          priceRange={card.node.content.product.priceRange}
          comparePriceRange={card.node.content.product.compareAtPriceRange}
          image={card.node.content.product.image}
          url={card.node.content.product.canonicalUrl
            ? card.node.content.product.canonicalUrl
            : url + "/" + card.node.content.product.handle}
          badge={card.node.content.product.badge}
          showPrice={card.node.content.product.showPrice}
          {fontVariant}
          {paddingLg}
          {imageConfig}
          sizeGuide={findSizeGuideCategory(
            card?.node?.content?.product?.sizeGuide,
            card?.node?.content?.product?.tags,
          )}
        />
      {/if}
      {#if card.node.type === CardType.Editorial && card.node.content}
        <EditorialCard
          title={card.node.content.title}
          image={card.node.content.image}
          ctaText={card.node.content.cta && card.node.content.cta.text}
          url={card.node.content.cta && card.node.content.cta.href}
          {fontVariant}
          {paddingLg}
        />
      {/if}
    {/each}
  </div>
{/if}
